import { OutlineMainHeader } from './outline-main-header';

/* Modify Drupal markup  */
export const modifyMarkup = (self: OutlineMainHeader, seed: string) => {
  const mainButtons = self.querySelectorAll('.primary-nav__button-toggle');
  const topLevelItems = self.querySelectorAll(
    '.primary-nav__menu-item--level-1'
  );
  const subLevelMenu = self.querySelectorAll('.primary-nav__menu--level-2');

  /* Add ID and modify button markup */
  if (topLevelItems.length) {
    topLevelItems.forEach((li, index) => {
      li.id = `${seed}-main-item--${index + 1}`;
      modifyDesktopMainButtonMarkup(li as HTMLLIElement);
    });
  }
  /* Add button ID, click listener (de-duplicated), and save text as data-label attribute */
  if (mainButtons.length) {
    mainButtons.forEach((button, index) => {
      type ButtonWithHandler = HTMLButtonElement & { _menuClickHandler?: EventListener };

      const btn = button as ButtonWithHandler;
      const labelText = btn.querySelector('.primary-nav__menu-link')?.innerHTML;

      btn.id = `${seed}-button--${index + 1}`;
      btn.setAttribute('aria-controls', `${seed}-sub--${index + 1}`);

      // Remove any existing handler before binding a new one
      if (btn._menuClickHandler) {
        btn.removeEventListener('click', btn._menuClickHandler);
      }
      const handler = () => mainButtonClickHandler(btn);
      btn.addEventListener('click', handler);
      btn._menuClickHandler = handler;

      if (labelText) {
        btn.setAttribute('data-label', labelText);
      }
    });
  }
  /* Add submenu ID, modify sub-list markup to create primary link. */
  if (subLevelMenu.length) {
    subLevelMenu.forEach((ul, index) => {
      ul.id = `${seed}-sub--${index + 1}`;
      if (ul.children.length) {
        ul.children[0].classList.add('primary-link');
        if (ul.children.length > 7) {
          const subElements = Array.from(ul.children).slice(1);
          const sectionEl = document.createElement('section');

          sectionEl.classList.add('desktop-columns');
          subElements.forEach(el => sectionEl.appendChild(el));
          ul.appendChild(sectionEl);
        }
      }
    });
  }

  /* Setup appropriate handlers based on current mode */
  if (!self.isMobile) {
    setupDesktopFocusHandlers(self);
  } else {
    mainButtons.forEach(button => button.setAttribute('mobile', 'mobile'));
  }

  /* Setup search panel focus handler */
  setupSearchPanelFocusHandler(self);
};

/* Setup handler for search panel focus out */
export const setupSearchPanelFocusHandler = (self: OutlineMainHeader) => {
  // First remove any existing handlers
  removeSearchPanelFocusHandler(self);

  const searchPanel = self.shadowRoot?.getElementById('outline-main-header--search-panel');
  if (searchPanel) {
    // Create a handler function and store it as a data attribute for later removal
    const handler = (event: Event) => {
      const focusEvent = event as FocusEvent;

      // Only proceed if search is open
      if (!self.searchOpen) return;

      // Get the related target (where focus is moving to)
      const relatedTarget = focusEvent.relatedTarget as Node;

      // Don't close if there's no relatedTarget (e.g., when clicking)
      if (!relatedTarget) return;

      // Check if the focus is moving outside the search panel and the header component
      const isWithinHeaderShadow =
        self.shadowRoot?.contains(relatedTarget) ?? false;
      const isMovingOutside =
        !searchPanel.contains(relatedTarget) &&
        !self.contains(relatedTarget) &&
        !isWithinHeaderShadow;

      if (isMovingOutside) {
        // Close the search panel when focus moves outside
        self.searchOpen = false;
      }
    };

    // Store the handler reference on the element using a custom property
    const panelWithHandler = searchPanel as HTMLElement & {
      _focusoutHandler?: EventListener;
    };
    panelWithHandler._focusoutHandler = handler;

    // Add a focusout event listener to the search panel
    searchPanel.addEventListener('focusout', handler);
  }
};

/* Remove search panel focus handler */
export const removeSearchPanelFocusHandler = (self: OutlineMainHeader) => {
  const searchPanel = self.shadowRoot?.getElementById('outline-main-header--search-panel');

  if (searchPanel) {
    // If we have a stored handler, remove it
    const panelWithHandler = searchPanel as HTMLElement & { _focusoutHandler?: EventListener };
    if (panelWithHandler._focusoutHandler) {
      searchPanel.removeEventListener('focusout', panelWithHandler._focusoutHandler);
      panelWithHandler._focusoutHandler = undefined;
    }
  }
};

/* Search panel focus trap - ensure keyboard focus stays within the panel */
export const setupSearchPanelFocusTrap = (self: OutlineMainHeader) => {
  // Remove any existing focus trap
  removeSearchPanelFocusTrap(self);

  // Only set up the focus trap if the search panel is open
  if (!self.searchOpen) return;

  const searchPanel = self.shadowRoot?.getElementById('outline-main-header--search-panel');
  if (searchPanel) {
    // Get the slotted search panel content
    const slottedSearchPanel = self.querySelector('[slot="search-panel"]');
    if (!slottedSearchPanel) return;

    // Find all focusable elements within the search panel
    const focusableElements = getFocusableElements(slottedSearchPanel);
    if (focusableElements.length === 0) return;

    // Look for the close button in the shadow DOM and add it to our focusable elements
    const closeButton = slottedSearchPanel.shadowRoot?.querySelector('.outline-search--close-button');
    if (closeButton && closeButton instanceof HTMLElement) {
      const style = window.getComputedStyle(closeButton);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        focusableElements.unshift(closeButton);
      }
    }

    // Create and store the keydown handler on the search panel element
    const handler = (event: Event) => {
      const keyEvent = event as KeyboardEvent;
      if (keyEvent.key !== 'Tab') return;

      // Get first and last focusable elements
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Handle Tab and Shift+Tab to cycle within the focus trap
      if (keyEvent.shiftKey) {
        // If Shift+Tab on first element, move to last element
        if (hasDeepFocus(firstElement)) {
          console.log('First element');
          keyEvent.preventDefault();
          lastElement.focus();
        }
      } else {
        // If Tab on last element, move to first element
        if (hasDeepFocus(lastElement)) {
          keyEvent.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Store the handler on the search panel for later removal
    const panelWithHandler = searchPanel as HTMLElement & { _focusTrapHandler?: EventListener };
    panelWithHandler._focusTrapHandler = handler;

    // Add the keydown event listener to the search panel
    searchPanel.addEventListener('keydown', handler);

    // Focus the first input element if it exists, otherwise focus on the first focusable element
    setTimeout(() => {
      const firstInputElement = focusableElements.find(el => el.tagName === 'INPUT');
      if (firstInputElement) {
        firstInputElement.focus();
      } else {
        focusableElements[0].focus();
      }
    }, 0);
  }
};

/* Remove search panel focus trap */
export const removeSearchPanelFocusTrap = (self: OutlineMainHeader) => {
  const searchPanel = self.shadowRoot?.getElementById('outline-main-header--search-panel');

  if (searchPanel) {
    // If we have a stored handler, remove it
    const panelWithHandler = searchPanel as HTMLElement & { _focusTrapHandler?: EventListener };
    if (panelWithHandler._focusTrapHandler) {
      searchPanel.removeEventListener('keydown', panelWithHandler._focusTrapHandler);
      panelWithHandler._focusTrapHandler = undefined;
    }

    // If search panel is closed, return focus to the search button
    if (!self.searchOpen) {
      // Get the search button based on the current display mode (mobile or desktop)
      const searchButton = self.shadowRoot?.querySelector(
        self.isMobile
          ? '.outline-main-header--search-toggle button'
          : '.outline-main-header--search button'
      ) as HTMLElement;

      if (searchButton) {
        // Focus the search button if <li> elements are not 'open'
        const navSlot = self.shadowRoot?.querySelector('slot[name="nav"]') as HTMLSlotElement;
        const assignedNodes = navSlot?.assignedNodes() as HTMLElement[];

        // Get the <nav>
        const navElement = assignedNodes?.find(node => node.tagName === 'NAV') as HTMLElement;

        if (navElement) {
          // Look for <li [open]>
          const openListItems = navElement.querySelectorAll('li[open]');
          if (openListItems.length > 0) {
            return;
          }
        }

        // Otherwise, focus the search button
        setTimeout(() => {
          searchButton.focus();
        }, 0);
      }
    }
  }
};

/* Helper function to get all focusable elements within a container */
export const getFocusableElements = (container: Element): HTMLElement[] => {
  // Collect all focusable elements
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'area[href]',
    'iframe',
    'object',
    'embed',
    '[contenteditable]'
  ].join(',');

  return Array.from(container.querySelectorAll(selector))
    .filter(el => {
      // Filter out hidden elements
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }) as HTMLElement[];
};

// Helper function to check if target element has focus (even through shadow DOM)
export const hasDeepFocus = (element: Element): boolean => {
  let activeElement = document.activeElement;

  // Check if the element itself has focus
  if (activeElement === element) {
    return true;
  }

  // Check through shadow DOM
  while (activeElement && activeElement.shadowRoot && activeElement.shadowRoot.activeElement) {
    activeElement = activeElement.shadowRoot.activeElement;
    if (activeElement === element) {
      return true;
    }
  }

  return false;
};

/* Setup focus handlers for desktop menu items */
const setupDesktopFocusHandlers = (self: OutlineMainHeader) => {
  // First remove any existing handlers
  removeDesktopFocusHandlers(self);

  const topLevelItems = self.querySelectorAll('.primary-nav__menu-item--level-1');

  topLevelItems.forEach(item => {
    const subMenu = item.querySelector('.primary-nav__menu--level-2');
    if (subMenu) {
      // Create a handler function and store it as a data attribute for later removal
      const handler = (event: Event) => {
        const focusEvent = event as FocusEvent;
        // Check if the focus is moving outside of this menu item
        const isMovingOutside = !item.contains(focusEvent.relatedTarget as Node);

        if (isMovingOutside && item.hasAttribute('open')) {
          // Close the submenu when focus moves outside
          item.removeAttribute('open');
          const button = item.querySelector('.primary-nav__button-toggle');
          if (button) {
            button.setAttribute('aria-expanded', 'false');
          }
        }
      };

      // Store the handler reference on the element using a custom property
      const itemWithHandler = item as HTMLElement & { _focusoutHandler?: EventListener };
      itemWithHandler._focusoutHandler = handler;

      // Add a focusout event listener to the top-level item
      item.addEventListener('focusout', handler);
    }
  });
};

/* Remove focus handlers from desktop menu items */
const removeDesktopFocusHandlers = (self: OutlineMainHeader) => {
  const topLevelItems = self.querySelectorAll('.primary-nav__menu-item--level-1');

  topLevelItems.forEach(item => {
    // If we have a stored handler, remove it
    const itemWithHandler = item as HTMLElement & { _focusoutHandler?: EventListener };
    if (itemWithHandler._focusoutHandler) {
      item.removeEventListener('focusout', itemWithHandler._focusoutHandler);
      itemWithHandler._focusoutHandler = undefined;
    }
  });
};

/* Modify main button markup */
const modifyDesktopMainButtonMarkup = (self: HTMLLIElement) => {
  const button = self.querySelector('.primary-nav__button-toggle');
  const textSpan = self.querySelector('.primary-nav__menu-link--level-1');
  if (button && textSpan) {
    // Only append if the span is not already inside the button
    if (!button.contains(textSpan)) {
      button.append(textSpan);
    }
  }
};

/* Main button click handler, toggles logic between desktop and mobile logic. */
const mainButtonClickHandler = (self: HTMLButtonElement) => {
  const isMobile = self.hasAttribute('mobile');
  isMobile
    ? mainButtonClickHandlerMobile(self)
    : mainButtonClickHandlerDesktop(self);
};

/* Main button click handler logic for desktop. Handles open attribute and aria-expanded  */
const mainButtonClickHandlerDesktop = (self: HTMLButtonElement) => {
  const parentLi = self.parentElement;

  if (parentLi && parentLi.tagName === 'LI') {
    if (parentLi.hasAttribute('open')) {
      parentLi.removeAttribute('open');
      self.setAttribute('aria-expanded', 'false');
    } else {
  removeAllOpen(true);
      self.setAttribute('aria-expanded', 'true');
      parentLi.setAttribute('open', 'open');
    }
  }
};

/* Main button click handler logic for mobile. Handles aria-expanded,
   open attributes and classes, handles the back button text change,
   and handles animation logic. */
const mainButtonClickHandlerMobile = (self: HTMLButtonElement) => {
  const parentElements = document.querySelectorAll(
    '.primary-nav__menu-item--level-1'
  );
  const flyaways = document.querySelectorAll('.flyaway');
  const parentLi = self.parentElement;

  if (parentLi) {
    if (parentLi?.hasAttribute('open')) {
      parentLi.removeAttribute('open');
      self.setAttribute('aria-expanded', 'false');
      self.classList.remove('is-active-mobile');
      if (flyaways.length) {
        clearAnimationClasses(flyaways);
      }
    } else if (!parentLi?.hasAttribute('open')) {
      const elementsToHide = [...parentElements].filter(
        el => el.id !== parentLi.id
      ) as unknown as NodeListOf<Element>;
      parentLi.setAttribute('open', 'mobile-open');
      self.classList.add('is-active-mobile');
      self.setAttribute('aria-expanded', 'true');
      applyAnimationCLasses(elementsToHide);
    }
  }
  handleMobileButtonTextChange(self);
};

/* Removes all open states */
export const removeAllOpen = (closeSearch: boolean = true) => {
  const self = document.querySelector(
    'outline-main-header'
  ) as OutlineMainHeader;
  const openElements = self?.querySelectorAll('[open]');

  if (openElements && openElements.length) {
    openElements.forEach(el => {
      if (el.children[0] && el.children[0].tagName === 'BUTTON') {
        const openButtonEL = el.children[0] as HTMLButtonElement
        mainButtonClickHandler(openButtonEL)
      } else {
        el.removeAttribute('open');
      }
    });
  }
  /* Close Search if clicked off*/
  if (closeSearch && self) {
    self.searchOpen = false;
  }

  clearAllFlyaway(self);
  self.scrollLock = false;
};

/* Closes all open elements on Escape */
export const handleESC = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    const self = document.querySelector(
      'outline-main-header'
    ) as OutlineMainHeader;
  removeAllOpen(true);
    self.searchOpen = false;

    // Close mobile menu if it's open
    if (self.mobileOpen) {
      // The focus trap will be removed by the updated() callback when mobileOpen changes
      self.mobileOpen = false;
      self.scrollLock = false;
    }
  }
};

/* Toggles mobile attributes, searchOpen property, and flyaways states when switching between mobile and desktop. */
export const resizeReset = (self: OutlineMainHeader) => {
  const mainButtons = self.querySelectorAll('.primary-nav__button-toggle');
  const openEls = self.querySelectorAll('[open]');

  if (openEls.length) {
  removeAllOpen(true);
  }

  // Clean up first
  clearAllFlyaway(self);

  if (!self.isMobile) {
    // Switching to desktop mode
    self.mobileOpen = false;
    self.searchOpen = false;
    self.scrollLock = false;
    mainButtons.forEach(button => button.removeAttribute('mobile'));

    // Remove mobile focus handlers and setup desktop ones
    removeDesktopFocusHandlers(self); // Clean slate first
    setupDesktopFocusHandlers(self);
  } else {
    // Switching to mobile mode
    mainButtons.forEach(button => button.setAttribute('mobile', 'mobile'));
    // Remove desktop focus handlers when in mobile mode
    removeDesktopFocusHandlers(self);
  }

  // Re-setup search panel focus handler for current mode
  setupSearchPanelFocusHandler(self);
};

const clearAllFlyaway = (self: OutlineMainHeader) => {
  const flyaways = self.querySelectorAll('.flyaway');
  if (flyaways.length) {
    clearAnimationClasses(flyaways);
  }
}

/* Adds mobile open animation classes at correct time */
const applyAnimationCLasses = (elements: NodeListOf<Element>) => {
  elements.forEach(el => {
    el.classList.add('flyaway');
    setTimeout(() => el.classList.add('shrink'), 300);
  });
};

/* Removes animation classes in reverse order and time */
const clearAnimationClasses = (elements: NodeListOf<Element>) => {
  elements.forEach(el => {
    el.classList.remove('shrink');
    setTimeout(() => el.classList.remove('flyaway'), 100);
  });
};

/* Toggles the mobile main button text and classes between open anc closed states */
const handleMobileButtonTextChange = (self: HTMLButtonElement) => {
  const isActiveMobile = self.classList.contains('is-active-mobile');
  const hiddenLabel = self.querySelector('.visually-hidden') as HTMLSpanElement;
  const visibleLabel = self.querySelector(
    '.primary-nav__menu-link--level-1'
  ) as HTMLSpanElement;
  const dataLabel = self.getAttribute('data-label');

  if (isActiveMobile) {
    visibleLabel.innerHTML = 'Back to main menu';
    hiddenLabel.innerHTML = 'Back to main menu';
  } else {
    if (dataLabel && dataLabel !== null) {
      hiddenLabel.innerHTML = dataLabel;
      visibleLabel.innerHTML = dataLabel;
    }
  }
};

/* Handles closing all elements when the header is not clicked on. */
export const clickAwayCloseHandler = (e: Event) => {
  const self = document.querySelector('outline-main-header') as OutlineMainHeader;

  // Don't proceed if we don't have a header component
  if (!self) return;
  // Check if the click target is outside the header component

  const isClickOutsideHeader = !self.contains(e.target as Node);

  // Check if autocomplete list exists
  const autocompleteList = document.querySelector('ul.search-api-autocomplete-search');
  const hasAutocompleteItems =
    !!autocompleteList && autocompleteList.querySelectorAll('li').length > 0;

  // Don't close if suggestions are visible
  if (hasAutocompleteItems) return;

  // If click is outside header and search is open, close the search panel
  if (isClickOutsideHeader && self.searchOpen) {
    self.searchOpen = false;
     // Prevent event propagation to avoid multiple handlers
    e.stopPropagation();
  }
};

/* Mobile menu focus trap - ensure keyboard focus stays within the menu while it's open */
export const setupMobileMenuFocusTrap = (self: OutlineMainHeader) => {
  // Remove any existing focus trap
  removeMobileMenuFocusTrap(self);

  // Only set up the focus trap if the mobile menu is open
  if (!self.mobileOpen) return;

  const mobileMenu = self.shadowRoot?.getElementById('outline-main-header--mobile-menu');
  if (mobileMenu) {
    // Create a combined list of focusable elements
    let focusableElements: HTMLElement[] = [];

    // Get the focusable elements from the shadow DOM
    focusableElements = getFocusableElements(mobileMenu);

    // Get slotted nav content
    const navSlotted = self.querySelector('[slot="nav"]');
    if (navSlotted) {
      focusableElements = [...focusableElements, ...getFocusableElements(navSlotted)];
    }

    // Get slotted utility content
    const utilitySlotted = self.querySelector('[slot="utility"]');
    if (utilitySlotted) {
      focusableElements = [...focusableElements, ...getFocusableElements(utilitySlotted)];
    }

    // Get slotted CTA content
    const ctaSlotted = self.querySelector('[slot="cta"]');
    if (ctaSlotted) {
      focusableElements = [...focusableElements, ...getFocusableElements(ctaSlotted)];
    }

    if (focusableElements.length === 0) return;

    // Add the mobile menu toggle button to the focusable elements (should be first in tab order)
    const menuToggleButton = self.shadowRoot?.querySelector('.outline-main-header--menu-toggle button') as HTMLElement;
    if (menuToggleButton) {
      // Make sure the toggle button is first in the tab order
      focusableElements.unshift(menuToggleButton);
    }

    // Create and store the keydown handler on the mobile menu element
    const handler = (event: Event) => {
      const keyEvent = event as KeyboardEvent;
      if (keyEvent.key !== 'Tab') return;

      // Get first and last focusable elements
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Handle Tab and Shift+Tab to cycle within the focus trap
      if (keyEvent.shiftKey) {
        // If Shift+Tab on first element, move to last element
        if (hasDeepFocus(firstElement)) {
          keyEvent.preventDefault();
          lastElement.focus();
        }
      } else {
        // If Tab on last element, move to first element
        if (hasDeepFocus(lastElement)) {
          keyEvent.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Store the handler on the mobile menu for later removal
    const menuWithHandler = mobileMenu as HTMLElement & { _focusTrapHandler?: EventListener };
    menuWithHandler._focusTrapHandler = handler;

    // Add the keydown event listener to the mobile menu
    self.addEventListener('keydown', handler);

    // Focus the menu toggle button when the menu opens
    setTimeout(() => {
      menuToggleButton.focus();
    }, 0);
  }
};

/* Remove mobile menu focus trap */
export const removeMobileMenuFocusTrap = (self: OutlineMainHeader) => {
  const mobileMenu = self.shadowRoot?.getElementById('outline-main-header--mobile-menu');

  if (mobileMenu) {
    // If we have a stored handler, remove it
    const menuWithHandler = mobileMenu as HTMLElement & { _focusTrapHandler?: EventListener };
    if (menuWithHandler._focusTrapHandler) {
      self.removeEventListener('keydown', menuWithHandler._focusTrapHandler);
      menuWithHandler._focusTrapHandler = undefined;
    }
  }
};

export const mobileMenuDisplayHandler = (self: OutlineMainHeader) => {
  self.mobileOpen = !self.mobileOpen;
  self.scrollLock = !self.scrollLock;
  // Focus trap is now handled by the component's updated() method
};
